import { useState, useEffect } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { Snackbar } from '@mui/material'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useGetYearForFooter } from 'utils/hooks'

import { selectSelfConstruct } from 'store/selfConstruct/selfConstructSlice'
import { MaxWidthWrapper } from 'components/common/ContentWrapper'
import MobileServiceItem from './MobileServiceItem'
import DesktopServiceItem from './DesktopServiceItem'
import FooterLinks from './FooterLinks'
import SocialMediaBlock from './SocialMediaBlock'
import SinyiResources from './SinyiResources'
import { indexPageMaxContentWidth } from 'config/theme'
import {
  ARTICLE_ALBUM_LINKS,
  ABOUT_SINYI_LINKS,
  SINYI_GROUP_LINKS,
  SOCIAL_MEDIA_LINKS,
} from './FooterData'

const FooterWrap = styled(Box)`
  ${({ theme }) => `
    background-color: ${theme.palette.background.gray};
    padding: ${theme.spacing(10)} 0;
  `}
`

const GridLayout = styled(MaxWidthWrapper)`
  ${({ theme }) => `
    display: grid;
    grid-auto-rows: auto;
    max-width: ${indexPageMaxContentWidth};

    grid-template-columns: 1fr;
    grid-row-gap: ${theme.spacing(4)};

    padding-left: ${theme.spacing(4)};
    padding-right: ${theme.spacing(4)};

    ${theme.breakpoints.up('md')} {
      padding-left: ${theme.spacing(8)};
      padding-right: ${theme.spacing(8)};
    }

    ${theme.breakpoints.up('lg')} {
      grid-template-columns: repeat(5, 1fr);
      grid-column-gap: ${theme.spacing(6)};
      grid-row-gap: 0;
    }

    ${theme.breakpoints.up('xl')} {
      padding-left: ${theme.spacing(12)};
      padding-right: ${theme.spacing(12)};
    }
  `}
`

const SinyiTrackGridItem = styled.div`
  ${({ theme }) => `
    grid-column: 1;
    ${theme.breakpoints.up('lg')} {
      grid-column: 4 / 6;
    }
  `}
`

const Footer = ({ ...rest }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const selfConstruct = useSelector(selectSelfConstruct)
  const currentYear = useGetYearForFooter()
  const [toast, setToast] = useState({ open: false, message: '' })
  const [isCsr, setCsr] = useState(false)

  useEffect(() => {
    // We hope footer re-render according to isMobile while client side is ready
    setCsr(true)
  }, [])

  return (
    <FooterWrap {...rest}>
      <GridLayout>
        <FooterLinks title="文章圖輯" links={ARTICLE_ALBUM_LINKS} />
        <FooterLinks title="關於信義居家" links={ABOUT_SINYI_LINKS} />
        <FooterLinks
          title="信義集團"
          links={SINYI_GROUP_LINKS}
          bottomComponent={<SinyiResources />}
        />
        <SinyiTrackGridItem>
          <SocialMediaBlock
            title="追蹤信義居家"
            socialLinks={SOCIAL_MEDIA_LINKS}
          />
        </SinyiTrackGridItem>
      </GridLayout>

      <Box
        my={6}
        maxWidth={indexPageMaxContentWidth}
        mx="auto"
        px={{
          xs: 4,
          md: 8,
          xl: 12,
        }}
      >
        <Divider />
      </Box>

      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        maxWidth={indexPageMaxContentWidth}
        margin="0 auto"
        px={{
          xs: 4,
          md: 8,
          xl: 12,
        }}
      >
        <Box
          order={isMobile ? 2 : 1}
          alignSelf={isMobile ? 'center' : 'auto'}
          mt={isMobile ? 10 : 0}
        >
          <Image src="/img/logo_with_text.svg" width={148} height={54} alt="" />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          color="text.minus2"
          order={isMobile ? 1 : 2}
          ml={isMobile ? 0 : 7.5}
        >
          <Box component="span">服務專線&emsp;(02)2722-3589</Box>
          <Box component="span" mt={2}>
            服務時間&emsp;週一～五 9:00-12:00、13:30-18:00
          </Box>
          <Box component="span" mt={2}>
            客服信箱&emsp;living@sinyi.com.tw
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          color="text.secondary"
          alignSelf={isMobile ? 'center' : 'auto'}
          alignItems={isMobile ? 'center' : 'flex-end'}
          mt={isMobile ? 4 : 0}
          flex={isMobile ? 0 : 1}
          order={3}
        >
          <Box component="span">110 台北市信義區信義路五段100號</Box>
          <Box component="span" mt={2}>
            {`© ${currentYear} 信義房屋股份有限公司 版權所有`}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={toast.message}
        onClose={() => setToast({ open: false, message: '' })}
      />
    </FooterWrap>
  )
}

export default Footer
